import { useEffect } from 'react';

const SITE_NAME = 'AutoTrust Ghana';
const SITE_URL = 'https://autotrustgh.com';
const DEFAULT_DESCRIPTION = 'Ghana\'s #1 trusted car and spare parts marketplace. AI-verified listings, secure escrow payments, mobile money accepted. Buy and sell vehicles safely with zero scams.';
const DEFAULT_IMAGE = '/og-image.png';

// Target keywords for SEO
const PRIMARY_KEYWORDS = 'buy cars Ghana, cars for sale Ghana, used cars Ghana, car marketplace Ghana, sell car Ghana, cheap cars Ghana, second hand cars Ghana, car spare parts Ghana';

// Custom hook for SEO - avoids react-helmet compatibility issues with React 19
function useSEO({ title, description, image, url, type = 'website', keywords = '', structuredData = null }) {
  useEffect(() => {
    const siteUrl = window.location.origin;
    const pageTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Buy & Sell Cars in Ghana`;
    const pageUrl = url || window.location.href;
    const imageUrl = image?.startsWith('http') ? image : `${siteUrl}${image || DEFAULT_IMAGE}`;
    const desc = description || DEFAULT_DESCRIPTION;
    const allKeywords = keywords ? `${keywords}, ${PRIMARY_KEYWORDS}` : PRIMARY_KEYWORDS;

    // Update document title
    document.title = pageTitle;

    // Update or create meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta
    updateMeta('description', desc);
    updateMeta('keywords', allKeywords);
    updateMeta('author', 'AutoTrust Ghana');
    updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Open Graph
    updateMeta('og:type', type, true);
    updateMeta('og:url', pageUrl, true);
    updateMeta('og:title', pageTitle, true);
    updateMeta('og:description', desc, true);
    updateMeta('og:image', imageUrl, true);
    updateMeta('og:image:width', '1200', true);
    updateMeta('og:image:height', '630', true);
    updateMeta('og:site_name', SITE_NAME, true);
    updateMeta('og:locale', 'en_GH', true);

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:site', '@autotrustgh');
    updateMeta('twitter:creator', '@autotrustgh');
    updateMeta('twitter:url', pageUrl);
    updateMeta('twitter:title', pageTitle);
    updateMeta('twitter:description', desc);
    updateMeta('twitter:image', imageUrl);

    // Geo targeting for Ghana
    updateMeta('geo.region', 'GH');
    updateMeta('geo.placename', 'Ghana');
    updateMeta('geo.position', '7.9465;-1.0232');
    updateMeta('ICBM', '7.9465, -1.0232');
    
    // Language
    updateMeta('language', 'English');
    updateMeta('content-language', 'en-GH');

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl.split('?')[0]); // Remove query params for canonical

    // Add alternate hreflang
    let hreflang = document.querySelector('link[rel="alternate"][hreflang="en-gh"]');
    if (!hreflang) {
      hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', 'en-gh');
      document.head.appendChild(hreflang);
    }
    hreflang.setAttribute('href', pageUrl.split('?')[0]);

    // Structured data
    if (structuredData) {
      let script = document.querySelector('script[data-seo="structured-data"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo', 'structured-data');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    // Add Organization schema to all pages
    let orgScript = document.querySelector('script[data-seo="organization"]');
    if (!orgScript) {
      orgScript = document.createElement('script');
      orgScript.setAttribute('type', 'application/ld+json');
      orgScript.setAttribute('data-seo', 'organization');
      document.head.appendChild(orgScript);
      orgScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "AutoTrust Ghana",
        "alternateName": "AutoTrust GH",
        "url": SITE_URL,
        "logo": `${SITE_URL}/logo-full.png`,
        "description": DEFAULT_DESCRIPTION,
        "areaServed": { "@type": "Country", "name": "Ghana" },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "support@autotrustgh.com"
        }
      });
    }
  }, [title, description, image, url, type, keywords, structuredData]);
}

export function SEO({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  keywords = '',
  listing = null,
  article = null,
  structuredData = null
}) {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : SITE_URL;
  const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const imageUrl = image?.startsWith('http') ? image : `${siteUrl}${image}`;

  // Generate structured data
  const getStructuredData = () => {
    if (structuredData) return structuredData;
    
    if (listing) {
      const listingSchema = {
        "@context": "https://schema.org",
        "@type": listing.listing_type === 'part' ? "Product" : "Vehicle",
        "name": listing.title,
        "description": listing.description,
        "image": listing.photos?.[0] || listing.external_photos?.[0] || imageUrl,
        "url": pageUrl,
        "brand": listing.make ? { "@type": "Brand", "name": listing.make } : undefined,
        "model": listing.model,
        "vehicleModelDate": listing.year?.toString(),
        "mileageFromOdometer": listing.mileage ? {
          "@type": "QuantitativeValue",
          "value": listing.mileage,
          "unitCode": "KMT"
        } : undefined,
        "fuelType": listing.fuel_type,
        "vehicleTransmission": listing.transmission,
        "color": listing.color,
        "itemCondition": listing.condition === 'New' 
          ? "https://schema.org/NewCondition" 
          : "https://schema.org/UsedCondition",
        "offers": {
          "@type": "Offer",
          "price": listing.price,
          "priceCurrency": "GHS",
          "availability": listing.status === 'sold' 
            ? "https://schema.org/SoldOut" 
            : "https://schema.org/InStock",
          "seller": {
            "@type": "Person",
            "name": listing.seller_name
          },
          "areaServed": {
            "@type": "City",
            "name": listing.location,
            "containedInPlace": { "@type": "Country", "name": "Ghana" }
          }
        }
      };
      
      // Add breadcrumb
      const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Browse", "item": `${SITE_URL}/browse` },
          { "@type": "ListItem", "position": 3, "name": listing.title, "item": pageUrl }
        ]
      };
      
      return [listingSchema, breadcrumb];
    }
    
    if (article) {
      return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.featured_image,
        "datePublished": article.created_at,
        "dateModified": article.updated_at || article.created_at,
        "author": {
          "@type": "Organization",
          "name": "AutoTrust Ghana"
        },
        "publisher": {
          "@type": "Organization",
          "name": "AutoTrust Ghana",
          "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo-full.png` }
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl }
      };
    }
    
    return null;
  };

  useSEO({
    title,
    description,
    image,
    url,
    type,
    keywords,
    structuredData: getStructuredData()
  });

  return null; // This component doesn't render anything
}

// Pre-configured SEO for common pages
export function HomeSEO() {
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AutoTrust Ghana",
    "url": SITE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE_URL}/browse?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
  
  return (
    <SEO 
      title="Buy & Sell Cars and Spare Parts in Ghana"
      description="Ghana's #1 trusted car marketplace. Browse 1000+ AI-verified cars and spare parts. Secure escrow payments, mobile money accepted. Buy Toyota, Honda, Hyundai & more with confidence."
      keywords="buy cars Ghana, sell car Ghana, used cars Accra, car marketplace Ghana, cheap cars Ghana, Toyota Ghana, Honda Ghana"
      structuredData={homeSchema}
    />
  );
}

export function BrowseSEO({ filters }) {
  const parts = [];
  let keywords = '';
  
  if (filters?.listing_type === 'part') {
    parts.push('Car Spare Parts');
    keywords = 'car spare parts Ghana, auto parts, car accessories';
  } else if (filters?.listing_type === 'car') {
    parts.push('Cars');
    keywords = 'cars for sale Ghana, buy cars, used cars';
  } else {
    parts.push('Cars & Parts');
    keywords = 'cars for sale Ghana, car spare parts, buy cars';
  }
  
  if (filters?.make) {
    parts.push(filters.make);
    keywords += `, ${filters.make} Ghana, ${filters.make} for sale`;
  }
  if (filters?.location) {
    parts.push(`in ${filters.location}`);
    keywords += `, cars ${filters.location}, buy cars ${filters.location}`;
  }
  
  const title = `Browse ${parts.join(' ')} for Sale in Ghana`;
  const description = `Find verified ${parts[0].toLowerCase()} for sale in Ghana. Compare prices, check AI trust scores, and buy with secure escrow. Mobile money accepted.`;
  
  return <SEO title={title} description={description} keywords={keywords} />;
}

export function ListingSEO({ listing }) {
  if (!listing) return null;
  
  const title = listing.listing_type === 'car'
    ? `${listing.year} ${listing.make} ${listing.model} for Sale in ${listing.location}`
    : listing.title;
    
  const description = listing.listing_type === 'car'
    ? `Buy this ${listing.year} ${listing.make} ${listing.model} in ${listing.location}, Ghana for GHS ${listing.price?.toLocaleString()}. ${listing.mileage ? `${listing.mileage.toLocaleString()} km` : ''} ${listing.transmission || ''} ${listing.fuel_type || ''}. AI-verified, secure escrow.`
    : `${listing.title} - ${listing.part_category} for sale in ${listing.location}. GHS ${listing.price?.toLocaleString()}. Verified seller, secure payment.`;
  
  const keywords = listing.listing_type === 'car'
    ? `${listing.make} ${listing.model} Ghana, ${listing.year} ${listing.make}, buy ${listing.make} ${listing.location}, used ${listing.make} Ghana`
    : `${listing.part_category} Ghana, car parts ${listing.location}, ${listing.title}`;

  // Server-composited OG/Twitter card (brand + photo + price overlay).
  // Falls back to listing photo if endpoint is unavailable.
  const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const ogImage = listing.listing_id
    ? `${backend}/api/og/listing/${listing.listing_id}.png`
    : (listing.photos?.[0] || listing.external_photos?.[0]);

  return (
    <SEO 
      title={title}
      description={description}
      keywords={keywords}
      image={ogImage}
      type="product"
      listing={listing}
    />
  );
}

export function BlogSEO({ article }) {
  if (!article) return null;
  
  return (
    <SEO 
      title={article.title}
      description={article.excerpt}
      keywords={`${article.category || 'cars'} Ghana, car buying tips, auto news Ghana`}
      image={article.featured_image}
      type="article"
      article={article}
    />
  );
}

export function CitySEO({ city, type = 'car' }) {
  const isParts = type === 'part';
  const title = isParts 
    ? `Car Spare Parts in ${city}, Ghana`
    : `Cars for Sale in ${city}, Ghana`;
  const description = isParts
    ? `Find quality car spare parts and accessories in ${city}. Verified sellers, secure payments, mobile money accepted. Shop auto parts on AutoTrust Ghana.`
    : `Browse ${city}'s best car deals. AI-verified listings, secure escrow payments. Find Toyota, Honda, Hyundai and more in ${city}, Ghana.`;
  const keywords = isParts
    ? `car parts ${city}, auto parts ${city}, spare parts ${city} Ghana`
    : `cars for sale ${city}, buy cars ${city}, used cars ${city}, cheap cars ${city} Ghana`;
    
  return (
    <SEO 
      title={title}
      description={description}
      keywords={keywords}
    />
  );
}

export default SEO;
